// RvModal wraps a native <dialog> so a page can hand a large form all the room it
// needs. Island Architecture: a light-DOM web component, so the <form> inside the
// dialog still submits normally and HTMX keeps working on the elements within.
//
// Usage:
//   <button type="button" data-modal-open="gen-modal">Nueva Plantilla</button>
//   <rv-modal>
//     <dialog id="gen-modal" class="modal modal--wide"> … <button data-modal-close>✕</button> … </dialog>
//   </rv-modal>
//
// Add the `open-on-load` attribute to open it as soon as it mounts (used when the
// server renders the page already in "edit" mode).
export class RvModal extends HTMLElement {
  private dialog: HTMLDialogElement | null = null;

  connectedCallback(): void {
    this.dialog = this.querySelector<HTMLDialogElement>("dialog");
    if (!this.dialog) return;

    // Open triggers can live anywhere on the page — and may be replaced by an
    // HTMX swap — so the listener is delegated at the document level.
    document.addEventListener("click", this.onDocumentClick);

    // Clicking the backdrop (the dialog element itself, outside its content).
    this.dialog.addEventListener("click", this.onDialogClick);

    if (this.hasAttribute("open-on-load") && !this.dialog.open) {
      this.dialog.showModal();
    }
  }

  disconnectedCallback(): void {
    document.removeEventListener("click", this.onDocumentClick);
    this.dialog?.removeEventListener("click", this.onDialogClick);
  }

  private onDocumentClick = (event: Event): void => {
    const dialog = this.dialog;
    if (!dialog) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest(`[data-modal-open="${dialog.id}"]`)) {
      event.preventDefault();
      if (!dialog.open) dialog.showModal();
      return;
    }
    // Close buttons only count when they belong to this dialog.
    const closer = target.closest("[data-modal-close]");
    if (closer && dialog.contains(closer)) {
      event.preventDefault();
      dialog.close();
    }
  };

  private onDialogClick = (event: MouseEvent): void => {
    // The dialog fills the viewport; a click landing on it (and not on the inner
    // panel) is a backdrop click.
    const dialog = this.dialog;
    if (dialog && event.target === dialog) dialog.close();
  };
}
